/**
 * 19. Journal Entries — list of journal entries by date/status/period with
 * total debits/credits and filters.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";
import { EntryNumber } from "./entry-number";

interface JournalEntryRow {
  id: string;
  entryNumber: string | null;
  entryDate: string;
  journalCode: string;
  narration: string | null;
  status: "Draft" | "Submitted" | "Approved" | "Posted" | "Reversed" | "Voided";
  totalDebits: string;
  totalCredits: string;
  currencyCode: string;
}

export function JournalEntriesPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<JournalEntryRow>(
    ["finance", "journal-entries"],
    "/finance/journal-entries",
  );

  return (
    <div>
      <PageHeader
        title="Journal Entries"
        subtitle="All journal entries by date, status, and period."
        actions={
          <PendingButton variant="primary" reason="journalEditor" slice="ED-W6">
            {t("documentActions.newJournalEntry")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Entries (${data.total})`}>
          <DataTable<JournalEntryRow>
            rows={data.items}
            columns={[
              {
                key: "entryNumber",
                label: "Number",
                render: (r) => (
                  <EntryNumber value={r.entryNumber} status={r.status} />
                ),
              },
              {
                key: "entryDate",
                label: "Date",
                render: (r) => formatDate(r.entryDate),
              },
              { key: "journalCode", label: "Journal" },
              { key: "narration", label: "Narration" },
              {
                key: "totalDebits",
                label: "Debits",
                numeric: true,
                render: (r) => formatMoney(r.totalDebits, r.currencyCode),
              },
              {
                key: "totalCredits",
                label: "Credits",
                numeric: true,
                render: (r) => formatMoney(r.totalCredits, r.currencyCode),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
